import { ComponentPropsWithoutRef, FC } from 'react';
import Markdown from 'shared/components/Markdown';
import { arrowStyles } from 'shared/styles/arrow';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  LINE_HEIGHTS,
  SPACINGS,
  TRANSITIONS,
} from 'shared/styles/variables';
import styled from 'styled-components';

import { isFocused } from 'design-system/lib/pseudoClass';
import FormControlProps from 'lib/interfaces/FormControlProps';
import { fontFamilyCss } from 'verify/lib/utils/font-family';

// ts doesn't handle the 'as' prop correctly
type SelectProps = ComponentPropsWithoutRef<'select'> & FormControlProps;

const Select = styled('select')<SelectProps>`
  appearance: none;
  &::-ms-expand {
    display: none;
  }

  background-color: ${COLORS.white};
  font-family: ${({ theme: { fontFamily } }) => fontFamilyCss(fontFamily)};
  font-size: ${FONT_SIZES.input};
  font-weight: ${FONT_WEIGHTS.regular};
  line-height: ${LINE_HEIGHTS.input};
  color: ${({ theme }) => theme.colorFont};
  padding: ${SPACINGS.base}px 0px ${SPACINGS.base}px ${SPACINGS.base / 2}px;
  border-bottom: ${({ theme }) => `1px solid ${theme.colorInputBorder}`};
  border-radius: 0;

  ${({ theme, hasError }) => hasError && `border-color: ${theme.colorError}`};

  &:focus {
    outline: none;
    border-color: ${({ theme, hasError, hasWarning }) => {
      if (hasError) {
        return theme.colorError;
      } else if (hasWarning) {
        return theme.colorWarning;
      } else {
        return theme.colorPrimary;
      }
    }};
  }

  position: relative;
  width: 100%;

  background-color: ${({ theme }) => theme.colorInputBackground};
`;

const Wrapper = styled('div')`
  position: relative;
  width: 100%;

  &::after {
    ${arrowStyles};
    right: ${SPACINGS.base}px;
  }
`;

const StyledMarkdown = styled(Markdown)<SelectProps & { focused?: boolean }>`
  font-weight: ${FONT_WEIGHTS.regular};
  line-height: ${LINE_HEIGHTS.input};
  color: ${({ theme }) => theme.colorInputBorder};
  position: absolute;
  pointer-events: none;
  left: ${SPACINGS.base / 2}px;
  transition: ${TRANSITIONS.fast} ease all;

  top: -14px;
  font-size: ${FONT_SIZES.small};

  label {
    margin-bottom: 0;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    color: inherit;
  }

  ${({ theme, hasError }) => hasError && `color: ${theme.colorError};`};

  ${Select}${isFocused} ~ & {
    color: ${({ theme, hasError, hasWarning }) => {
      if (hasError) {
        return theme.colorError;
      } else if (hasWarning) {
        return theme.colorWarning;
      } else {
        return theme.colorPrimary;
      }
    }};
  }
`;

interface MaterialSelectProps {
  labelText: string;
}

const MaterialSelect: FC<MaterialSelectProps & SelectProps> = ({
  labelText,
  hasError,
  hasWarning,
  className,
  ...props
}) => (
  <Wrapper className={className}>
    <Select hasError={hasError} hasWarning={hasWarning} {...props} />
    <StyledMarkdown
      wrapper='label'
      wrapperProps={{ htmlFor: props.id }}
      hasError={hasError}
      hasWarning={hasWarning}
    >
      {labelText}
    </StyledMarkdown>
  </Wrapper>
);
export default MaterialSelect;
